import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import branchIcon from "assets/images/icons/branch.svg";
// Checkbox Component for individual branch or city
const Checkbox = ({ isSelected, onClick, label, style }) => {
  return (
    <div
      onClick={onClick}
      className="d-flex align-items-center justify-content-end px-3 py-2 cursor-pointer hover-bg-light gap-3"
      style={{ cursor: "pointer", paddingLeft: style.paddingLeft }}
    >
      <span className={style.textClass}>{label}</span>
      <div
        style={{
          ...styles.checkCircle,
          backgroundColor: isSelected ? "#C4D26E" : "transparent",
          border: isSelected ? "none" : "2px solid #dee2e6",
        }}
      >
        {isSelected && <Check size={16} color="white" />}
      </div>
    </div>
  );
};

// Styles for the check circle
const styles = {
  checkCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "4px",
    border: "1px solid #BBBBBB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};

// Main Dropdown Component
const BranchDropdown = ({
  data,
  label = "Choose Branch",
  allLabel = "Choose All Branches",
  resetLabel = "Reset all",
  onSelectionChange,
  selectedData = [],
  handleApply = () => {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(selectedData);
  const dropdownRef = useRef(null);

  // Add event listener for clicks outside the dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    // Add event listener when dropdown is open
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const getCityBranchIds = (cityId) => {
    const city = data.find((item) => item.id === cityId);
    return city?.branches?.map((branch) => branch.id) || [];
  };

  const getAllBranchIds = () => {
    return data
      .filter((item) => item.type === "city")
      .flatMap((city) => city.branches.map((branch) => branch.id));
  };

  const isCityFullySelected = (cityId) => {
    const cityBranchIds = getCityBranchIds(cityId);
    return cityBranchIds.every((id) => selected.includes(id));
  };

  const handleSelect = (id, type) => {
    let newSelected = [...selected];

    if (id === "all") {
      const allBranchIds = getAllBranchIds();
      newSelected = selected.length === allBranchIds.length ? [] : allBranchIds;
    } else if (type === "city") {
      const cityBranchIds = getCityBranchIds(id);
      newSelected = isCityFullySelected(id)
        ? selected.filter((branchId) => !cityBranchIds.includes(branchId))
        : [...new Set([...selected, ...cityBranchIds])];
    } else {
      newSelected = selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id];
    }

    setSelected(newSelected);
    if (onSelectionChange) onSelectionChange(newSelected); // notify parent
  };

  const resetSelection = (e) => {
    e.stopPropagation();
    setSelected([]);
    if (onSelectionChange) onSelectionChange([]); // notify parent
  };

  const renderBranches = (city) => {
    return city.branches.map((branch) => (
      <Checkbox
        key={branch.id}
        isSelected={selected.includes(branch.id)}
        onClick={() => handleSelect(branch.id, "branch")}
        label={branch.name}
        style={{ paddingLeft: "2rem", textClass: "text_gray text-end" }}
      />
    ));
  };

  const renderCity = (city) => (
    <div key={city.id}>
      <Checkbox
        isSelected={isCityFullySelected(city.id)}
        onClick={() => handleSelect(city.id, "city")}
        label={city.name}
        style={{ paddingLeft: "1rem", textClass: "text_black text-end" }}
      />
      {renderBranches(city)}
    </div>
  );

  return (
    <div
      className="position-relative"
      style={{ minWidth: 141 }}
      ref={dropdownRef}
    >
      <button
        onClick={toggleDropdown}
        className="select_branch_btn btn btn-light w-100 d-flex align-items-center justify-content-between"
      >
        <div className="d-flex align-items-center gap-2 text-secondary">
          <img src={branchIcon} alt="icon" width={15} height={15} />
          <span className="fs_14">{label}</span>
        </div>
        <ChevronDown
          size={20}
          className="text_gray"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </button>

      {isOpen && (
        <div
          className="position-absolute mt-2 bg-white rounded shadow-sm border"
          style={{ zIndex: 1000, minWidth: 230, right: 0 }}
        >
          {data.length === 0 ? (
            <p className="text-center py-5">No options found</p>
          ) : (
            <>
              <div style={{ maxHeight: "256px", overflowY: "auto" }}>
                <Checkbox
                  isSelected={selected.length === getAllBranchIds().length}
                  onClick={() => handleSelect("all", "all")}
                  label={allLabel}
                  style={{ paddingLeft: "1rem", textClass: "text_black" }}
                />
                {data
                  .filter((item) => item.type === "city")
                  .map((city) => renderCity(city))}
              </div>

              <div className="border-top p-2 g_between">
                <button
                  onClick={resetSelection}
                  className="btn btn-link text-danger w-100 text-end text-decoration-none p-1"
                >
                  {resetLabel}
                </button>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    handleApply();
                  }}
                  className="btn btn-link text-success w-100 text-end text-decoration-none p-1"
                >
                  Apply Filter
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BranchDropdown;
